/**
 * FormattedNumberInput (Feb 2026)
 *
 * Drop-in replacement for `<input type="number" />`:
 * - Removes the native up/down stepper arrows.
 * - Displays the value with thousands separators + 2 decimals on blur
 *   (e.g. 1000 → "1,000.00").
 * - Shows the raw editable number on focus so the user types without commas.
 * - Passes a CLEAN numeric string to the caller via onChange so callers can
 *   keep doing `parseFloat(e.target.value)` unchanged — the DB stays numeric.
 *
 * API mirrors the native input:
 *   value, onChange(e), min, max, step, className, placeholder, disabled,
 *   readOnly, data-testid, onBlur, onFocus, onKeyDown, ...
 *
 * The `step` prop drives the decimal precision:
 *   step="1"        → 0 decimals
 *   step="0.1"      → 1 decimal
 *   step="0.01"/def → 2 decimals
 */
import React, { useState, useRef, useCallback } from "react";

const _dp = (step) => {
  if (step == null || step === "") return 2;
  const s = String(step);
  if (!s.includes(".")) return s === "1" ? 0 : 0;
  return Math.min(6, (s.split(".")[1] || "").length);
};

const _sanitize = (str) => {
  if (str == null) return "";
  // Keep only digits, one leading minus, and up to one dot.
  let s = String(str).replace(/,/g, "").replace(/\s+/g, "");
  // Strip anything but the allowed characters.
  s = s.replace(/[^\d.\-]/g, "");
  // Only one leading minus.
  const negative = s.startsWith("-");
  s = s.replace(/-/g, "");
  // Only first dot survives.
  const parts = s.split(".");
  if (parts.length > 1) s = parts.shift() + "." + parts.join("");
  return (negative ? "-" : "") + s;
};

const _fmt = (val, dp) => {
  if (val === "" || val == null) return "";
  const num = parseFloat(val);
  if (!Number.isFinite(num)) return "";
  return num.toLocaleString("en-US", {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
};

export const FormattedNumberInput = React.forwardRef(function FormattedNumberInput(
  { value, onChange, onFocus, onBlur, onKeyDown, step, min, max, className, disabled, readOnly, placeholder, ...rest },
  ref,
) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState("");
  const dp = _dp(step);
  const inputRef = useRef(null);
  const setRef = useCallback((el) => {
    inputRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  }, [ref]);

  const display = focused
    ? draft
    : _fmt(value, dp);

  const handleFocus = (e) => {
    setFocused(true);
    // Show raw digits on focus; if value is 0 keep it visible so user can overwrite.
    const raw = value === "" || value == null ? "" : String(value);
    setDraft(raw);
    // Select-all so typing replaces the value; keep behaviour opt-out via preventDefault.
    try { e.target.select(); } catch { /* noop */ }
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e) => {
    setFocused(false);
    // Clamp to [min, max] on blur so the stored number stays within bounds.
    let num = draft === "" || draft === "-" ? "" : parseFloat(draft);
    if (num !== "" && Number.isFinite(num)) {
      if (min !== undefined && min !== "" && num < parseFloat(min)) num = parseFloat(min);
      if (max !== undefined && max !== "" && num > parseFloat(max)) num = parseFloat(max);
      // Round to dp to match display precision so the DB never carries hidden precision.
      num = +num.toFixed(dp);
    }
    const nextValue = num === "" ? "" : String(num);
    if (String(value ?? "") !== nextValue && onChange) {
      // Pass through a synthetic event compatible with existing handlers using e.target.value.
      onChange({ target: { value: nextValue } });
    }
    if (onBlur) onBlur(e);
  };

  const handleChange = (e) => {
    const cleaned = _sanitize(e.target.value);
    setDraft(cleaned);
    // Propagate cleaned numeric string upstream so parseFloat(e.target.value) works.
    if (onChange) onChange({ target: { value: cleaned } });
  };

  return (
    <input
      ref={setRef}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={onKeyDown}
      className={className}
      disabled={disabled}
      readOnly={readOnly}
      placeholder={placeholder}
      data-nouppercase
      {...rest}
    />
  );
});

export default FormattedNumberInput;
